// Enhanced Google Workspace SSO integration with configurable session management
// Supports custom session durations while maintaining Google OAuth security

import { jwtDecode } from 'jwt-decode';
import { configService } from './config';
import type { AdminUser } from '@/types/admin';
import { createLogger } from '@/utils/logger';

const logger = createLogger('AuthService');

interface GoogleCredentialResponse {
  credential: string;
}

interface GooglePromptNotification {
  isNotDisplayed(): boolean;
  isSkippedMoment(): boolean;
}

interface GoogleIdentityApi {
  initialize(config: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    auto_select: boolean;
    cancel_on_tap_outside: boolean;
  }): void;
  prompt(callback: (notification: GooglePromptNotification) => void): void;
  renderButton(
    element: HTMLElement,
    options: { theme: 'outline'; size: 'large'; type: 'standard'; width: string }
  ): void;
  disableAutoSelect(): void;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: GoogleIdentityApi;
      };
    };
    googleSignInResolve?: (user: AdminUser) => void;
    googleSignInReject?: (error: unknown) => void;
    queryClient?: {
      invalidateQueries: (options: { queryKey: readonly unknown[] }) => void;
    };
  }
}

interface GoogleTokenPayload {
  sub: string;
  email: string;
  name: string;
  picture?: string;
  hd?: string; // Hosted domain for Google Workspace
  exp: number;
  iat: number;
}

interface SessionData {
  user: AdminUser;
  sessionToken: string;
  refreshToken: string;
  googleToken?: string;
  sessionExpiry: number;
  createdAt: number;
}

interface GoogleSignInResponse {
  id_token: string;
  refresh_token: string;
  session_token: string;
  user: {
    id: string;
    email: string;
    name?: string;
    picture?: string;
  };
  expires_in: number;
}

interface SessionTokenResponse {
  session_token: string;
  expires_in: number;
}

class AuthService {
  private readonly clientId: string;
  private readonly allowedDomain: string;
  private sessionCheckTimer?: NodeJS.Timeout;
  private refreshInFlight?: Promise<boolean>;
  private readonly refreshThresholdMs = 10 * 60 * 1000;

  constructor() {
    this.clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
    this.allowedDomain = import.meta.env.VITE_ADMIN_AUTH_DOMAIN || 'nurie.ai';
    
    if (!this.clientId) {
      logger.error('Google Client ID not configured');
    }

    // Start session monitoring
    this.startSessionMonitoring();
  }

  /**
   * Initialize Google Sign-In
   */
  async initialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Load Google Identity Services script
      if (typeof window.google !== 'undefined') {
        this.initializeGoogleSignIn();
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.onload = () => {
        this.initializeGoogleSignIn();
        resolve();
      };
      script.onerror = () => reject(new Error('Failed to load Google Sign-In'));
      document.head.appendChild(script);
    });
  }

  private initializeGoogleSignIn(): void {
    const google = window.google;
    if (!google) {
      throw new Error('Google Sign-In not initialized');
    }

    google.accounts.id.initialize({
      client_id: this.clientId,
      callback: this.handleGoogleSignIn.bind(this),
      auto_select: false,
      cancel_on_tap_outside: true,
    });
  }

  /**
   * Trigger Google Sign-In popup
   */
  async signIn(): Promise<AdminUser> {
    return new Promise((resolve, reject) => {
      const google = window.google;
      if (!google) {
        reject(new Error('Google Sign-In not initialized'));
        return;
      }

      // Store resolve/reject for callback
      window.googleSignInResolve = resolve;
      window.googleSignInReject = reject;

      // Use Sign in with Google popup (provides ID tokens)
      google.accounts.id.prompt((notification: GooglePromptNotification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          // If prompt fails, fall back to programmatic sign-in
          logger.info('One Tap not available, using programmatic sign-in');
          
          // Create a temporary button element for programmatic sign-in
          const tempDiv = document.createElement('div');
          document.body.appendChild(tempDiv);
          
          google.accounts.id.renderButton(tempDiv, {
            theme: 'outline',
            size: 'large',
            type: 'standard',
            width: '300'
          });
          
          // Trigger click programmatically
          setTimeout(() => {
            const button = tempDiv.querySelector('div[role="button"]') as HTMLElement;
            if (button) {
              button.click();
            }
            // Clean up
            document.body.removeChild(tempDiv);
          }, 100);
        }
      });
    });
  }

  private async handleGoogleSignIn(response: GoogleCredentialResponse): Promise<void> {
    try {
      const user = await this.processGoogleToken(response.credential);
      window.googleSignInResolve?.(user);
    } catch (error) {
      window.googleSignInReject?.(error);
    }
  }


  private async processGoogleToken(token: string): Promise<AdminUser> {
    try {
      const payload = jwtDecode<GoogleTokenPayload>(token);
      
      // Verify domain restriction
      if (!this.isValidDomain(payload.email)) {
        throw new Error(`Access restricted to ${this.allowedDomain} domain (or DEFAULT_ADMIN)`);
      }

      const authResponse = await this.exchangeGoogleToken(token);

      const user: AdminUser = {
        email: authResponse.user?.email || payload.email,
        name: authResponse.user?.name || payload.name,
        user_id: payload.sub,
        domain: payload.hd || this.extractDomain(payload.email),
      };

      const now = Date.now();
      const sessionExpiry = now + (authResponse.expires_in * 1000);
      const sessionData: SessionData = {
        user,
        sessionToken: authResponse.session_token,
        refreshToken: authResponse.refresh_token,
        googleToken: authResponse.id_token,
        sessionExpiry,
        createdAt: now
      };

      this.storeSessionData(sessionData);
      
      // Schedule session management
      this.scheduleSessionCheck();

      const sessionHours = Math.round(((sessionExpiry - now) / (60 * 60 * 1000)) * 10) / 10;
      logger.info('Sign in successful for:', user.email, `(${sessionHours}h session)`);
      
      // Record login activity in the backend (don't await to avoid blocking authentication)
      this.recordLoginActivity().catch(error => {
        logger.warn('Failed to record login activity:', error);
      });
      
      // Dispatch auth-change event immediately since localStorage is synchronous
      window.dispatchEvent(new Event('auth-change'));

      return user;
    } catch (error) {
      throw new Error(`Invalid token: ${error}`);
    }
  }

  private isValidDomain(email: string): boolean {
    const domain = this.extractDomain(email);
    const defaultAdmin = import.meta.env.VITE_DEFAULT_ADMIN || '';
    
    // Allow default admin email if configured
    if (defaultAdmin && email === defaultAdmin) {
      return true;
    }
    
    return domain === this.allowedDomain;
  }

  private extractDomain(email: string): string {
    return email.split('@')[1];
  }

  private storeToken(token: string, skipEvent: boolean = false): void {
    localStorage.setItem('admin_token', token);
    if (!skipEvent) {
      window.dispatchEvent(new Event('auth-change'));
    }
  }

  private storeSessionData(sessionData: SessionData, skipEvent: boolean = false): void {
    localStorage.setItem('admin_session', JSON.stringify(sessionData));
    localStorage.setItem('admin_session_token', sessionData.sessionToken);
    localStorage.setItem('admin_refresh_token', sessionData.refreshToken);
    localStorage.setItem('admin_session_expiry', sessionData.sessionExpiry.toString());
    localStorage.setItem('admin_user', JSON.stringify(sessionData.user)); // Keep for backwards compatibility
    this.storeToken(sessionData.sessionToken, skipEvent);
  }

  /**
   * Start session monitoring for custom session timeout
   */
  private startSessionMonitoring(): void {
    // Check session validity every 5 minutes
    this.sessionCheckTimer = setInterval(() => {
      this.checkSessionValidity();
    }, 5 * 60 * 1000);

    // Initial check
    this.checkSessionValidity();
  }

  /**
   * Check if current session is still valid
   */
  private checkSessionValidity(): void {
    const sessionData = this.getSessionData();
    
    if (!sessionData) {
      return; // No session to check
    }

    const now = Date.now();
    const timeRemaining = sessionData.sessionExpiry - now;
    
    if (timeRemaining <= 0) {
      logger.info('Session has expired, signing out');
      this.signOut();
      return;
    }

    if (timeRemaining <= this.refreshThresholdMs) {
      void this.refreshSession();
    }
  }

  /**
   * Schedule session check timer
   */
  private scheduleSessionCheck(): void {
    if (this.sessionCheckTimer) {
      clearInterval(this.sessionCheckTimer);
    }
    
    this.startSessionMonitoring();
  }

  /**
   * Get stored session data
   */
  private getSessionData(): SessionData | null {
    try {
      const sessionStr = localStorage.getItem('admin_session');
      if (!sessionStr) return null;
      const parsed = JSON.parse(sessionStr) as SessionData;
      if (!parsed.sessionToken && parsed.googleToken) {
        parsed.sessionToken = parsed.googleToken;
      }
      return parsed;
    } catch (error) {
      logger.warn('Failed to parse session data:', error);
      localStorage.removeItem('admin_session');
      return null;
    }
  }


  /**
   * Get stored token
   */
  getToken(): string | null {
    return localStorage.getItem('admin_session_token') || localStorage.getItem('admin_token');
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const sessionData = this.getSessionData();
    
    if (!sessionData) {
      return false;
    }

    const now = Date.now();
    
    // Check if session has expired
    if (now >= sessionData.sessionExpiry) {
      logger.info('Session expired during authentication check');
      this.signOut();
      return false;
    }

    return true;
  }

  /**
   * Get current user from session
   */
  getCurrentUser(): AdminUser | null {
    const sessionData = this.getSessionData();
    
    if (!sessionData) {
      return null;
    }

    const now = Date.now();
    
    // Check if session has expired
    if (now >= sessionData.sessionExpiry) {
      logger.info('Session expired while getting current user');
      this.signOut();
      return null;
    }

    return sessionData.user;
  }

  /**
   * Sign out user
   */
  signOut(): void {
    // Check if user data exists to determine if we should dispatch events
    // Avoid calling isAuthenticated() to prevent recursion
    const sessionData = this.getSessionData();
    const wasAuthenticated = !!sessionData;
    
    // Clear all session data
    localStorage.removeItem('admin_session');
    localStorage.removeItem('admin_token'); // Keep for backwards compatibility
    localStorage.removeItem('admin_session_token');
    localStorage.removeItem('admin_refresh_token');
    localStorage.removeItem('admin_session_expiry');
    localStorage.removeItem('admin_user'); // Keep for backwards compatibility
    
    // Clear timers
    if (this.sessionCheckTimer) {
      clearInterval(this.sessionCheckTimer);
      this.sessionCheckTimer = undefined;
    }

    // Sign out from Google
    if (window.google) {
      window.google.accounts.id.disableAutoSelect();
    }

    // Only fire auth-change event if we were actually authenticated
    if (wasAuthenticated) {
      logger.info('User signed out');
      window.dispatchEvent(new Event('auth-change'));
    }
  }

  /**
   * Get authorization header for API calls
   */
  getAuthHeader(): Record<string, string> {
    const sessionData = this.getSessionData();
    
    if (!sessionData) {
      return {};
    }

    const now = Date.now();
    
    // Check if session has expired
    if (now >= sessionData.sessionExpiry) {
      logger.info('Session expired while getting auth header');
      this.signOut();
      return {};
    }

    return { Authorization: `Bearer ${sessionData.sessionToken}` };
  }

  /**
   * Get session time remaining in milliseconds
   */
  getSessionTimeRemaining(): number {
    const sessionData = this.getSessionData();
    
    if (!sessionData) {
      return 0;
    }

    const now = Date.now();
    return Math.max(0, sessionData.sessionExpiry - now);
  }

  /**
   * Get session expiry date
   */
  getSessionExpiry(): Date | null {
    const sessionData = this.getSessionData();
    
    if (!sessionData) {
      return null;
    }

    return new Date(sessionData.sessionExpiry);
  }

  /**
   * Extend current session (if valid)
   */
  async extendSession(): Promise<boolean> {
    const refreshed = await this.refreshSession();
    if (refreshed) {
      const sessionData = this.getSessionData();
      if (sessionData) {
        logger.info('Session extended to:', new Date(sessionData.sessionExpiry).toLocaleString());
      }
    }
    return refreshed;
  }

  private lastLoginRecorded = 0;

  /**
   * Record login activity in the backend and update user with last login info
   * Rate limited to avoid excessive API calls
   */
  private async recordLoginActivity(): Promise<void> {
    // Rate limit: only record login activity once per minute to avoid spam
    const now = Date.now();
    if (now - this.lastLoginRecorded < 60 * 1000) {
      return;
    }
    this.lastLoginRecorded = now;
    try {
      // Import adminApiService here to avoid circular dependencies
      const { adminApiService } = await import('./adminApi');
      const response = await adminApiService.recordLoginActivity();
      
      // Check if the response indicates success
      if (response.status === 'success' && response.data?.last_login_at) {
        logger.info('Login activity recorded');
        
        // Update the stored user object with the last login information
        this.updateUserWithLastLogin(response.data.last_login_at);
        
        // Invalidate admin users cache so the updated last_login shows immediately
        try {
          const { adminUserKeys } = await import('../hooks/useAdminUsers');
          const { queryClient } = window;
          
          if (queryClient) {
            queryClient.invalidateQueries({ queryKey: adminUserKeys.lists() });
          }
          
          // Dispatch event for any components listening for login activity
          window.dispatchEvent(new CustomEvent('login-activity-recorded', { 
            detail: { timestamp: response.data?.timestamp || new Date().toISOString() }
          }));
        } catch {
          // Fallback: just dispatch the event
          window.dispatchEvent(new CustomEvent('login-activity-recorded', { 
            detail: { timestamp: new Date().toISOString() }
          }));
        }
      }
      
    } catch (error) {
      logger.warn('Failed to record login activity:', error);
      // Login tracking failure should not block authentication
    }
  }

  /**
   * Update the stored user object with last login information
   */
  private updateUserWithLastLogin(lastLoginAt: string): void {
    try {
      const sessionData = this.getSessionData();
      if (!sessionData) {
        logger.warn('No session data found to update last login');
        return;
      }

      // Update the user object with last login information
      const updatedUser: AdminUser = {
        ...sessionData.user,
        last_login: lastLoginAt
      };

      // Update session data
      const updatedSessionData: SessionData = {
        ...sessionData,
        user: updatedUser
      };

      // Store updated session data
      this.storeSessionData(updatedSessionData, true);
      
      // Silently update - avoid spamming logs for each login update
      
      // Dispatch auth-change event to notify components of the updated user data
      window.dispatchEvent(new Event('auth-change'));
      
    } catch (error) {
      logger.warn('Failed to update user with last login:', error);
    }
  }

  /**
   * Refresh user profile information from the backend (including last login)
   */
  async refreshUserProfile(): Promise<void> {
    try {
      if (!this.isAuthenticated()) {
        logger.info('Not authenticated, skipping profile refresh');
        return;
      }

      // Import adminApiService here to avoid circular dependencies
      const { adminApiService } = await import('./adminApi');
      const response = await adminApiService.verifyAdminAccess();
      
      if (response.admin_info?.last_login_at) {
        this.updateUserWithLastLogin(response.admin_info.last_login_at);
        // Profile refreshed silently to avoid log spam
      }
      
    } catch (error) {
      logger.warn('Failed to refresh user profile:', error);
      // Profile refresh failure should not break authentication
    }
  }

  private async exchangeGoogleToken(credential: string): Promise<GoogleSignInResponse> {
    const response = await fetch(`${configService.apiBaseUrl}/api/v1/auth/google/signin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Client': 'MediaHub-Ops-Dashboard',
        'X-Client-Version': configService.appVersion,
      },
      body: JSON.stringify({ credential }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to exchange Google token: ${response.status} ${errorText}`);
    }

    return response.json();
  }

  async refreshSession(): Promise<boolean> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    this.refreshInFlight = this.performSessionRefresh();
    try {
      return await this.refreshInFlight;
    } finally {
      this.refreshInFlight = undefined;
    }
  }

  private async performSessionRefresh(): Promise<boolean> {
    const sessionData = this.getSessionData();
    const refreshToken = sessionData?.refreshToken || localStorage.getItem('admin_refresh_token');
    if (!refreshToken) {
      return false;
    }

    try {
      const response = await fetch(`${configService.apiBaseUrl}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Client': 'MediaHub-Ops-Dashboard',
          'X-Client-Version': configService.appVersion,
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        return false;
      }

      const data = (await response.json()) as SessionTokenResponse;
      if (!data.session_token || !data.expires_in) {
        return false;
      }

      const now = Date.now();
      const updatedSessionData: SessionData = {
        ...(sessionData ?? {
          user: JSON.parse(localStorage.getItem('admin_user') || 'null'),
          refreshToken,
          sessionToken: data.session_token,
          sessionExpiry: now + (data.expires_in * 1000),
          createdAt: now,
        }),
        sessionToken: data.session_token,
        refreshToken,
        sessionExpiry: now + (data.expires_in * 1000),
      };

      if (!updatedSessionData.user) {
        return false;
      }

      this.storeSessionData(updatedSessionData);
      return true;
    } catch (error) {
      logger.warn('Token refresh failed:', error);
      return false;
    }
  }
}

export const authService = new AuthService();
export default authService;



